import { useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { C, FONT_MONO, hexA } from "@/theme";

/**
 * Menu de contexto de usuario (botao direito / "⋮"), no estilo do menu de
 * perfil do Discord. Componente burro: so desenha e dispara `onAction` — quem
 * usa decide o que cada acao faz de verdade (perfil, DM, silenciar etc).
 */
export type UserMenuAction =
  | "PROFILE"
  | "MENTION"
  | "MESSAGE"
  | "CALL"
  | "SET_VOLUME"
  | "TOGGLE_MUTE"
  | "COPY_INVITE_LINK"
  | "REMOVE_FRIEND"
  | "IGNORE"
  | "BLOCK"
  | "ASSIGN_ROLE"
  | "MOVE_TO_CHANNEL"
  | "SERVER_VOICE_MUTE"
  | "SERVER_AUDIO_DISABLE"
  | "DISCONNECT"
  | "KICK"
  | "BAN"
  | "TIMEOUT"
  | "COPY_USER_ID";

/** Durações oferecidas no submenu de timeout. */
export const TIMEOUT_OPTIONS: { id: string; label: string; ms: number | null }[] = [
  { id: "5m", label: "5 minutos", ms: 5 * 60_000 },
  { id: "1h", label: "1 hora", ms: 60 * 60_000 },
  { id: "1d", label: "1 dia", ms: 24 * 60 * 60_000 },
  { id: "off", label: "Tirar o timeout", ms: null },
];

export type RoleOption = { id: string; name: string; color: string; selected: boolean };
export type ChannelOption = { id: string; name: string };

export interface UserContextMenuProps {
  userId: string;
  username: string;
  isFriend: boolean;
  isSelf: boolean;
  serverPermissions: {
    canMute: boolean;
    canDeafen: boolean;
    canMoveMembers: boolean;
    canManageRoles: boolean;
    canModerate: boolean;
  };
  onAction: (action: UserMenuAction, userId: string) => void;

  /**
   * Extensoes alem do contrato pedido: o slider/toggles/submenus precisam de
   * um valor e de uma lista pra fazer alguma coisa de verdade — sem isso
   * `onAction` sozinho so avisa "aconteceu", nao carrega o dado. Tudo aqui e
   * opcional; sem eles o item correspondente some ou vira so um disparo de
   * `onAction` sem estado (degrada, nao quebra).
   */
  x: number;
  y: number;
  onClose: () => void;
  volumePercent?: number; // 0..200
  onVolumeChange?: (userId: string, percent: number) => void;
  muted?: boolean;
  roleOptions?: RoleOption[];
  onAssignRole?: (userId: string, roleId: string) => void;
  channelOptions?: ChannelOption[];
  onMoveToChannel?: (userId: string, channelId: string) => void;
  /** Já ignorado/bloqueado por quem abriu o menu — reflete o estado atual, o clique alterna. */
  ignored?: boolean;
  blocked?: boolean;
  /**
   * Silêncio e surdez de servidor JÁ aplicados a esta pessoa.
   *
   * O clique sempre alternou, mas o rótulo era fixo em "Silenciar": não
   * dava para saber, olhando o menu, se a pessoa estava silenciada — só
   * clicando e vendo o que acontecia.
   */
  serverMuted?: boolean;
  serverDeafened?: boolean;
  /** Em timeout agora — o submenu passa a oferecer tirar. */
  timedOut?: boolean;
  /**
   * Estou numa sala de voz, então dá para chamar alguém para ela.
   *
   * "Chamar" existia no menu desde sempre e caía no `default` do switch,
   * respondendo "essa ação ainda não existe" — um botão que só dava erro.
   */
  canInviteToVoice?: boolean;
  /** Duração escolhida no submenu de timeout; `null` tira o timeout. */
  onTimeout?: (userId: string, untilMs: number | null) => void;
}

/** Ponto que marca a ação já aplicada, no acento do servidor. */
function EstadoAtivo(): ReactElement {
  return (
    <span
      aria-hidden
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: "var(--ac, #1F4FD8)",
        flex: "0 0 auto",
      }}
    />
  );
}

const MENU_WIDTH = 232;
const SUBMENU_WIDTH = 200;
/** Respiro mínimo entre o menu e a borda da janela. */
const BORDA = 8;
const RADIUS = 8;

function Sep(): ReactElement {
  return <hr style={{ margin: "6px 0", border: 0, borderTop: `1px solid ${C.lineSoft}` }} />;
}

function Row({
  label,
  danger,
  disabled,
  onClick,
  onMouseEnter,
  icon,
  trailing,
}: {
  label: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  icon?: ReactNode;
  trailing?: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={disabled ? undefined : "hov-bg-1f"}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 8px",
        border: 0,
        borderRadius: 6,
        background: "transparent",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        textAlign: "left",
        color: danger ? C.red : C.textStrong,
        fontSize: 13,
      }}
    >
      {icon && (
        <span style={{ flex: "0 0 auto", width: 15, display: "grid", placeItems: "center" }}>{icon}</span>
      )}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {trailing}
    </button>
  );
}

function Chevron(): ReactElement {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" style={{ flex: "0 0 auto" }}>
      <path d="M9 6l6 6-6 6" stroke={C.textDim} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ToggleSwitch({ on, onToggle }: { on: boolean; onToggle: () => void }): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={event => {
        event.stopPropagation();
        onToggle();
      }}
      style={{
        width: 32,
        height: 18,
        flex: "0 0 auto",
        position: "relative",
        padding: 0,
        border: 0,
        borderRadius: 999,
        cursor: "pointer",
        background: on ? "var(--ac, #1F4FD8)" : C.track,
        transition: "background 150ms ease",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          width: 14,
          height: 14,
          borderRadius: 999,
          background: C.text,
          transition: "left 150ms ease",
          left: on ? 16 : 2,
        }}
      />
    </button>
  );
}

/** Submenu lateral simples (lista de cargos/salas) — abre no hover do item pai. */
/**
 * Submenu (Cargos, Mover para, Timeout).
 *
 * `lado` existe porque o padrão — abrir para a direita — nasce fora da tela
 * quando o menu foi aberto perto da borda direita, que é justamente onde o
 * painel de membros fica. O submenu aparecia, mas fora da área visível: dava
 * para ver a setinha e não dava para escolher cargo nenhum.
 *
 * `topo` desloca na vertical: um submenu longo ancorado numa linha lá
 * embaixo transbordaria pelo rodapé.
 */
function Submenu({
  items,
  onPick,
  lado = "direita",
}: {
  items: { id: string; label: ReactNode }[];
  onPick: (id: string) => void;
  lado?: "direita" | "esquerda";
}): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const [desloca, setDesloca] = useState(0);

  // O lado é decidido pelo pai, que sabe onde o menu está. O deslocamento
  // vertical não dá: depende de em QUE LINHA do menu este submenu nasceu, e
  // isso só o próprio elemento sabe, depois de existir. Daí medir aqui.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const caixa = node.getBoundingClientRect();
    const sobra = caixa.bottom - (window.innerHeight - BORDA);
    // Sobe só o que passou, e nunca acima do topo da janela.
    if (sobra > 0) setDesloca(-Math.min(sobra, Math.max(0, caixa.top - BORDA)));
  }, [items.length]);

  return (
    <div
      ref={ref}
      style={{
        position: "absolute",
        ...(lado === "direita"
          ? { left: "100%", marginLeft: 4 }
          : { right: "100%", marginRight: 4 }),
        top: -6 + desloca,
        width: SUBMENU_WIDTH,
        maxHeight: 260,
        overflowY: "auto",
        padding: 6,
        border: `1px solid ${C.lineStrong}`,
        borderRadius: RADIUS,
        background: C.raised,
        boxShadow: "0 14px 34px rgba(0,0,0,.5)",
        animation: "pop 120ms ease",
      }}
    >
      {items.length === 0 ? (
        <div style={{ padding: "7px 8px", fontSize: 12, color: C.textDim }}>Nada disponível.</div>
      ) : (
        items.map(item => <Row key={item.id} label={item.label} onClick={() => onPick(item.id)} />)
      )}
    </div>
  );
}

export function UserContextMenu(props: UserContextMenuProps): ReactElement {
  const {
    userId,
    username,
    isFriend,
    isSelf,
    serverPermissions,
    onAction,
    x,
    y,
    onClose,
    volumePercent,
    onVolumeChange,
    muted,
    roleOptions,
    onAssignRole,
    channelOptions,
    onMoveToChannel,
    ignored,
    blocked,
    serverMuted,
    serverDeafened,
    timedOut,
    canInviteToVoice,
    onTimeout,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<"roles" | "channel" | "timeout" | null>(null);
  /** Expulsar e banir pedem um segundo clique; este e o que esta armado. */
  const [confirmando, setConfirmando] = useState<"kick" | "ban" | null>(null);
  const podeModerar = serverPermissions.canModerate && !isSelf;

  // Posicionamento inteligente: nasce no ponto de clique, mas nunca sai da viewport.
  /**
   * Para que lado os submenus abrem.
   *
   * Decidido a partir da posicao JA CALCULADA do menu, nao do clique: o menu
   * pode ter sido puxado para dentro da tela pela medicao acima, e o que
   * importa para o submenu e onde ele acabou parando.
   *
   * Enquanto `pos` e null o menu ainda nao foi medido e nada esta na tela, entao
   * o valor daqui nao chega a ser usado.
   */
  const ladoSubmenu: "direita" | "esquerda" =
    pos !== null && pos.left + MENU_WIDTH + SUBMENU_WIDTH + BORDA > window.innerWidth
      ? "esquerda"
      : "direita";

  useLayoutEffect(() => {
    const measure = () => {
      const node = rootRef.current;
      const height = node?.offsetHeight ?? 320;
      const left = Math.min(x, window.innerWidth - MENU_WIDTH - BORDA);
      const top = Math.min(y, window.innerHeight - height - BORDA);
      setPos({ left: Math.max(BORDA, left), top: Math.max(BORDA, top) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [x, y]);

  // Fecha ao clicar fora ou apertar Esc.
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      if (rootRef.current?.contains(event.target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const fire = (action: UserMenuAction) => {
    onAction(action, userId);
    onClose();
  };

  const canRoles = serverPermissions.canManageRoles && !isSelf;
  const canMove = serverPermissions.canMoveMembers && !isSelf;
  const canServerMute = serverPermissions.canMute && !isSelf;
  const canServerAudio = serverPermissions.canDeafen && !isSelf;
  const canDisconnect = serverPermissions.canModerate && !isSelf;

  return createPortal(
    <div
      ref={rootRef}
      onContextMenu={event => event.preventDefault()}
      style={{
        position: "fixed",
        zIndex: 90,
        left: pos?.left ?? x,
        top: pos?.top ?? y,
        visibility: pos ? "visible" : "hidden",
        width: MENU_WIDTH,
        padding: 6,
        border: `1px solid ${C.lineStrong}`,
        borderRadius: RADIUS,
        background: C.raised,
        boxShadow: "0 14px 34px rgba(0,0,0,.5)",
        animation: "pop 120ms ease",
      }}
    >
      <div
        style={{
          padding: "6px 8px 8px",
          fontFamily: FONT_MONO,
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: ".08em",
          color: C.textLabelDim,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {username.toUpperCase()}
      </div>

      <Row label="Ver perfil" onClick={() => fire("PROFILE")} />
      {!isSelf && <Row label="Mencionar" onClick={() => fire("MENTION")} />}
      {!isSelf && <Row label="Enviar mensagem" onClick={() => fire("MESSAGE")} />}
      {!isSelf && canInviteToVoice && (
        <Row label="Chamar para a sala" onClick={() => fire("CALL")} />
      )}
      <Row label="Copiar ID do usuário" onClick={() => fire("COPY_USER_ID")} />

      {!isSelf && (serverPermissions.canMute || serverPermissions.canDeafen || onVolumeChange) && (
        <>
          <Sep />
          {onVolumeChange && (
            <div style={{ padding: "4px 8px 6px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11.5, color: C.textSoft }}>Volume</span>
                <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: C.textDim }}>
                  {volumePercent ?? 100}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={200}
                step={1}
                value={volumePercent ?? 100}
                onChange={event => onVolumeChange(userId, Number(event.target.value))}
                aria-label={`Volume de ${username}`}
                style={{ width: "100%", height: 4, margin: 0, accentColor: "var(--ac, #1F4FD8)", cursor: "pointer" }}
              />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 8px" }}>
            <span style={{ fontSize: 13, color: C.textStrong }}>Silenciar</span>
            <ToggleSwitch on={Boolean(muted)} onToggle={() => fire("TOGGLE_MUTE")} />
          </div>
        </>
      )}

      {(canRoles || canMove || canServerMute || canServerAudio || canDisconnect) && (
        <>
          <Sep />
          {canRoles && (
            <div
              style={{ position: "relative" }}
              onMouseEnter={() => setOpenSubmenu("roles")}
              onMouseLeave={() => setOpenSubmenu(current => (current === "roles" ? null : current))}
            >
              <Row
                label="Cargos"
                trailing={<Chevron />}
                onClick={() => {
                  if (!roleOptions) fire("ASSIGN_ROLE");
                }}
              />
              {openSubmenu === "roles" && roleOptions && (
                <Submenu
                  lado={ladoSubmenu}
                  items={roleOptions.map(role => ({
                    id: role.id,
                    label: (
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 999, background: role.color, flex: "0 0 auto" }} />
                        <span style={{ flex: 1, color: role.selected ? "var(--ac, #1F4FD8)" : C.textStrong }}>
                          {role.name}
                        </span>
                        {/* A marca diz que o cargo esta LIGADO — a pessoa pode ter varios. */}
                        <span style={{ flex: "0 0 auto", fontSize: 12, color: role.selected ? "var(--ac, #1F4FD8)" : C.textFaint }}>
                          {role.selected ? "✓" : ""}
                        </span>
                      </span>
                    ),
                  }))}
                  onPick={roleId => {
                    // Sem fechar: com cargos que somam, dar dois seguidos e o
                    // caso comum, e fechar obrigaria a reabrir o menu inteiro.
                    onAssignRole?.(userId, roleId);
                    onAction("ASSIGN_ROLE", userId);
                  }}
                />
              )}
            </div>
          )}
          {canMove && (
            <div
              style={{ position: "relative" }}
              onMouseEnter={() => setOpenSubmenu("channel")}
              onMouseLeave={() => setOpenSubmenu(current => (current === "channel" ? null : current))}
            >
              <Row
                label="Mover para"
                trailing={<Chevron />}
                onClick={() => {
                  if (!channelOptions) fire("MOVE_TO_CHANNEL");
                }}
              />
              {openSubmenu === "channel" && channelOptions && (
                <Submenu
                  lado={ladoSubmenu}
                  items={channelOptions.map(channel => ({ id: channel.id, label: channel.name }))}
                  onPick={channelId => {
                    onMoveToChannel?.(userId, channelId);
                    onAction("MOVE_TO_CHANNEL", userId);
                    onClose();
                  }}
                />
              )}
            </div>
          )}
          {canServerMute && (
            <Row
              label={serverMuted ? "Tirar o silêncio do servidor" : "Silenciar no servidor"}
              danger={!serverMuted}
              trailing={serverMuted ? <EstadoAtivo /> : undefined}
              onClick={() => fire("SERVER_VOICE_MUTE")}
            />
          )}
          {canServerAudio && (
            <Row
              label={serverDeafened ? "Reativar áudio no servidor" : "Desativar áudio no servidor"}
              danger={!serverDeafened}
              trailing={serverDeafened ? <EstadoAtivo /> : undefined}
              onClick={() => fire("SERVER_AUDIO_DISABLE")}
            />
          )}
          {canDisconnect && <Row label="Desconectar da chamada" danger onClick={() => fire("DISCONNECT")} />}
        </>
      )}

      {podeModerar && (
        <>
          <Sep />
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setOpenSubmenu("timeout")}
            onMouseLeave={() => setOpenSubmenu(current => (current === "timeout" ? null : current))}
          >
            <Row
              label={timedOut ? "Timeout (ativo)" : "Timeout"}
              trailing={<Chevron />}
              danger={!timedOut}
            />
            {openSubmenu === "timeout" && (
              <Submenu
                lado={ladoSubmenu}
                items={TIMEOUT_OPTIONS.filter(item => (timedOut ? true : item.ms !== null)).map(
                  item => ({ id: item.id, label: item.label }),
                )}
                onPick={id => {
                  const escolha = TIMEOUT_OPTIONS.find(item => item.id === id);
                  if (!escolha) return;
                  onTimeout?.(userId, escolha.ms === null ? null : Date.now() + escolha.ms);
                  onAction("TIMEOUT", userId);
                  onClose();
                }}
              />
            )}
          </div>
          {/*
            Expulsar e banir pedem confirmação NO PRÓPRIO menu: são as duas
            ações irreversíveis daqui, e num menu que abre no botão direito o
            clique errado é perto demais do certo.
          */}
          <Row
            label={confirmando === "kick" ? "Confirmar: expulsar?" : "Expulsar do servidor"}
            danger
            onClick={() => {
              if (confirmando !== "kick") {
                setConfirmando("kick");
                return;
              }
              fire("KICK");
            }}
          />
          <Row
            label={confirmando === "ban" ? "Confirmar: banir?" : "Banir do servidor"}
            danger
            onClick={() => {
              if (confirmando !== "ban") {
                setConfirmando("ban");
                return;
              }
              fire("BAN");
            }}
          />
        </>
      )}

      {!isSelf && (
        <>
          <Sep />
          <Row label="Copiar convite do servidor" onClick={() => fire("COPY_INVITE_LINK")} />
          {isFriend && <Row label="Remover amigo" danger onClick={() => fire("REMOVE_FRIEND")} />}
          <Row
            label={ignored ? "Parar de ignorar" : "Ignorar"}
            danger={!ignored}
            onClick={() => fire("IGNORE")}
          />
          <Row
            label={blocked ? "Desbloquear" : "Bloquear"}
            danger={!blocked}
            onClick={() => fire("BLOCK")}
          />
        </>
      )}
    </div>,
    document.body,
  );
}
